/**
 * useSessionRefresh Hook
 * 
 * Automatically refreshes Supabase session in the background to prevent token expiration.
 * This ensures users don't lose their session when idle for extended periods.
 * 
 * Features:
 * - Checks session validity every 5 minutes
 * - Refreshes token if expiring within 10 minutes
 * - Handles tab visibility changes (wake from sleep)
 * - Uses Page Visibility API to detect tab activation
 * - Handles errors gracefully
 * - Cleans up on unmount
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/clients/supabase';
import { getEnvironmentTimeouts } from '../../../shared/utils/environment';

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const REFRESH_THRESHOLD = 10 * 60; // Refresh if expiring within 10 minutes

// Network and environment-aware timeout calculation
const getNetworkTimeout = () => {
  const envTimeouts = getEnvironmentTimeouts();
  const baseTimeout = envTimeouts.session;
  
  // Check connection type if available
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  if (connection) {
    const effectiveType = connection.effectiveType;
    console.log('🌐 Network type detected:', effectiveType);
    
    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        return baseTimeout * 2; // 2x for slow connections
      case '3g':
        return baseTimeout * 1.5; // 1.5x for 3G
      case '4g':
      default:
        return baseTimeout; // Base timeout for fast connections
    }
  }
  
  // Default to base environment timeout
  return baseTimeout;
};

export const useSessionRefresh = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckRef = useRef<number>(Date.now());
  const consecutiveFailures = useRef<number>(0);
  const lastSessionCheck = useRef<{ timestamp: number; session: any } | null>(null);

  const checkAndRefreshSession = async (source: string = 'interval', retryCount: number = 0) => {
    const maxRetries = 2;
    const startTime = Date.now();
    
    try {
      const networkTimeout = getNetworkTimeout();
      console.log(`🔐 Checking session (source: ${source}, retry: ${retryCount}, timeout: ${networkTimeout}ms)...`);
      
      // Use cached session if recent (within 30 seconds) and not a retry
      const now = Date.now();
      if (retryCount === 0 && lastSessionCheck.current && 
          (now - lastSessionCheck.current.timestamp) < 30000) {
        console.log('📋 Using cached session data');
        const session = lastSessionCheck.current.session;
        
        if (session) {
          const expiresAt = session.expires_at;
          const timeUntilExpiry = expiresAt ? expiresAt - Math.floor(now / 1000) : 0;
          
          console.log('🔐 Cached session status:', {
            source,
            expiresAt: expiresAt ? new Date(expiresAt * 1000).toISOString() : 'unknown',
            timeUntilExpiry: `${Math.floor(timeUntilExpiry / 60)} minutes`,
            needsRefresh: timeUntilExpiry < REFRESH_THRESHOLD
          });
          
          // Only refresh if needed
          if (timeUntilExpiry < REFRESH_THRESHOLD) {
            console.log('🔄 Token expiring soon, refreshing session...');
            const refreshPromise = supabase.auth.refreshSession();
            const refreshTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Refresh timeout')), networkTimeout)
            );
            
            const { data: refreshData, error: refreshError } = await Promise.race([
              refreshPromise,
              refreshTimeoutPromise
            ]) as any;
            
            if (!refreshError && refreshData.session) {
              console.log('✅ Session refreshed successfully');
              lastSessionCheck.current = { timestamp: now, session: refreshData.session };
            }
          }
          
          consecutiveFailures.current = 0;
          lastCheckRef.current = now;
          return;
        }
      }
      
      // Add adaptive timeout based on network conditions
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session check timeout')), networkTimeout)
      );
      
      const { data: { session }, error } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]) as any;
      
      // Cache the session result
      lastSessionCheck.current = { timestamp: now, session };
      
      if (error) {
        console.error('❌ Error checking session:', error);
        
        // If it's an auth error, clear tokens
        const isAuthError = 
          error?.status === 401 || 
          error?.status === 403 ||
          error?.message?.includes('JWT') ||
          error?.message?.includes('expired') ||
          error?.message?.includes('invalid');
        
        if (isAuthError) {
          console.warn('⚠️ Auth error in session check - tokens may be invalid');
        }
        return;
      }
      
      if (!session) {
        console.log('ℹ️ No active session to refresh');
        return;
      }
      
      // Check if token is about to expire
      const expiresAt = session.expires_at;
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expiresAt ? expiresAt - nowInSeconds : 0;
      
      console.log('🔐 Session status:', {
        source,
        expiresAt: expiresAt ? new Date(expiresAt * 1000).toISOString() : 'unknown',
        timeUntilExpiry: `${Math.floor(timeUntilExpiry / 60)} minutes`,
        needsRefresh: timeUntilExpiry < REFRESH_THRESHOLD
      });
      
      // Refresh if expiring soon or already expired
      if (timeUntilExpiry < REFRESH_THRESHOLD) {
        console.log('🔄 Token expiring soon, refreshing session...');
        
        // Add adaptive timeout for refresh operation
        const refreshPromise = supabase.auth.refreshSession();
        const refreshTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Refresh timeout')), networkTimeout)
        );
        
        const { data: refreshData, error: refreshError } = await Promise.race([
          refreshPromise,
          refreshTimeoutPromise
        ]) as any;
        
        if (refreshError) {
          console.error('❌ Error refreshing session:', refreshError);
          
          // If refresh fails due to invalid token, user will be logged out by global error handler
          if (
            refreshError?.status === 401 ||
            refreshError?.message?.includes('expired') ||
            refreshError?.message?.includes('invalid')
          ) {
            console.warn('⚠️ Session refresh failed - token invalid or expired');
          }
          return;
        }
        
        if (refreshData.session) {
          console.log('✅ Session refreshed successfully');
          console.log('🔐 New expiry:', new Date((refreshData.session.expires_at || 0) * 1000).toISOString());
        }
      }
      
      // Reset failure counter on success
      consecutiveFailures.current = 0;
      lastCheckRef.current = Date.now();
      
      const duration = Date.now() - startTime;
      console.log(`✅ Session check completed in ${duration}ms`);
    } catch (err: any) {
      const duration = Date.now() - startTime;
      console.error('❌ Session refresh error:', err);
      console.error(`❌ Failed after ${duration}ms (timeout was ${getNetworkTimeout()}ms)`);
      
      // Increment failure counter
      consecutiveFailures.current++;
      
      // Retry on timeout or network errors
      if (retryCount < maxRetries && (
        err?.message?.includes('timeout') || 
        err?.message?.includes('network') ||
        err?.message?.includes('fetch')
      )) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
        console.log(`⏳ Retrying session check in ${delay}ms... (failures: ${consecutiveFailures.current})`);
        
        setTimeout(() => {
          checkAndRefreshSession(source, retryCount + 1);
        }, delay);
        return;
      }
      
      // If too many consecutive failures, increase check interval
      if (consecutiveFailures.current >= 3) {
        console.warn('⚠️ Multiple session check failures detected - reducing frequency');
      }
      
      // Don't throw error, just log it
      // Global error handler will handle auth errors from queries
    }
  };

  useEffect(() => {
    // Log environment info for debugging
    const envTimeouts = getEnvironmentTimeouts();
    console.log('🔧 Session refresh initialized:', {
      environment: {
        isVercel: window.location.hostname.includes('vercel.app'),
        hostname: window.location.hostname,
      },
      timeouts: envTimeouts,
      networkTimeout: getNetworkTimeout(),
    });
    
    // Delay initial check to avoid race condition with auth initialization
    const initialCheckDelay = setTimeout(() => {
      checkAndRefreshSession('initial');
    }, 1000); // 1 second delay

    // Set up periodic checks with adaptive interval
    const getCheckInterval = () => {
      // Increase interval if there are consecutive failures
      const baseInterval = CHECK_INTERVAL;
      const failureMultiplier = Math.min(consecutiveFailures.current, 3); // Max 3x slower
      return baseInterval * (1 + failureMultiplier * 0.5); // 1x, 1.5x, 2x, 2.5x
    };
    
    const scheduleNextCheck = () => {
      const interval = getCheckInterval();
      console.log(`⏰ Next session check in ${Math.round(interval / 60000)} minutes`);
      
      intervalRef.current = setTimeout(() => {
        checkAndRefreshSession('interval');
        scheduleNextCheck(); // Schedule the next check
      }, interval);
    };
    
    scheduleNextCheck();

    // Handle visibility change (tab becomes visible again)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastCheck = Date.now() - lastCheckRef.current;
        const minutesSinceLastCheck = Math.floor(timeSinceLastCheck / 60000);
        
        console.log(`👁️ Tab became visible (${minutesSinceLastCheck} minutes since last check)`);
        
        // If more than 2 minutes since last check, check immediately
        if (timeSinceLastCheck > 2 * 60 * 1000) {
          checkAndRefreshSession('visibility-change');
        }
      }
    };

    // Handle page show event (back from bfcache)
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('📄 Page restored from bfcache');
        checkAndRefreshSession('pageshow');
      }
    };

    // Handle focus event (window regains focus)
    const handleFocus = () => {
      const timeSinceLastCheck = Date.now() - lastCheckRef.current;
      
      // If more than 5 minutes since last check, check immediately
      if (timeSinceLastCheck > 5 * 60 * 1000) {
        console.log('🎯 Window focused after long idle');
        checkAndRefreshSession('focus');
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);

    // Cleanup on unmount
    return () => {
      clearTimeout(initialCheckDelay);
      if (intervalRef.current) {
        clearTimeout(intervalRef.current); // Changed from clearInterval to clearTimeout
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
};
